import React, { useState } from 'react';
import { registerAgent } from '../api';

const AGENT_IO_OPTIONS = [
  'application/json',
  'text/plain',
  'text/markdown',
  'application/pdf',
  'image/png',
  'image/jpeg',
  'multipart/form-data',
  'text/event-stream',
];

const DEFAULT_ARGS_SCHEMA = JSON.stringify({
  type: 'object',
  properties: {
    question: {
      type: 'string',
      description: 'Natural-language database question.'
    },
    advisor_id: {
      type: ['string', 'null'],
      description: 'Advisor/user identifier.'
    },
    cust_id: {
      type: ['string', 'null'],
      description: 'Customer identifier.'
    },
    use_case_id: {
      type: ['string', 'null'],
      description: 'Use case identifier.'
    },
    agent_id: {
      type: ['string', 'null'],
      description: 'Agent identifier for audit tracing.'
    },
    domain_id: {
      type: ['string', 'null'],
      description: 'Domain identifier for audit tracing.'
    },
    run_id: {
      type: ['string', 'null'],
      description: 'Trace identifier.'
    }
  },
  required: ['question']
}, null, 2);

const DEFAULT_RESPONSE_SCHEMA = JSON.stringify({
  type: 'object',
  properties: {
    query: {
      type: 'string',
      description: 'Generated SQL query.'
    },
    response: {
      type: 'string',
      description: 'Natural-language answer returned by the SQL tool wrapper.'
    }
  },
  required: ['query', 'response']
}, null, 2);

const newSkill = () => ({
  id: '',
  name: '',
  description: '',
  tags: '',
  examples: '',
  path: '',
  input_modes: ['application/json'],
  output_modes: ['application/json'],
  args_schema: DEFAULT_ARGS_SCHEMA,
  response_schema: DEFAULT_RESPONSE_SCHEMA,
});

// Convert a stored skill object (snake_case, arrays) → form skill state
const skillToForm = (s) => ({
  id: s.id ?? '',
  name: s.name ?? '',
  description: s.description ?? '',
  tags: Array.isArray(s.tags) ? s.tags.join(', ') : (s.tags ?? ''),
  examples: Array.isArray(s.examples) ? s.examples.join(', ') : (s.examples ?? ''),
   path: (s.path ?? '').trim(),
  input_modes: Array.isArray(s.input_modes) ? s.input_modes : ['application/json'],
  output_modes: Array.isArray(s.output_modes) ? s.output_modes : ['application/json'],
  args_schema: s.args_schema ? JSON.stringify(s.args_schema, null, 2) : DEFAULT_ARGS_SCHEMA,
  response_schema: s.response_schema ? JSON.stringify(s.response_schema, null, 2) : DEFAULT_RESPONSE_SCHEMA,
});

const agentToForm = (agent) => ({
  agent_type: agent.agent_type ?? 'internal',
  created_by: '',
  slug: agent.slug ?? '',
  name: agent.name ?? '',
  description: agent.description ?? '',
  publisher: agent.publisher ?? 'Internal',
  tags: Array.isArray(agent.tags) ? agent.tags.join(', ') : '',
  documentation_url: agent.documentation_url ?? '',
  is_listed: agent.is_listed ?? true,
  is_active: agent.is_active ?? true,
  version: agent.version ?? '1.0.0',
  transport: agent.transport ?? 'http',
  base_url: agent.base_url ?? '',
  streaming: agent.capabilities?.streaming ?? false,
  push_notifications: agent.capabilities?.push_notifications ?? false,
  state_transition_history: agent.capabilities?.state_transition_history ?? false,
  is_current: agent.is_current ?? true,
});

const DEFAULT_FORM = {
  agent_type: 'internal',
  created_by: '',
  slug: '',
  name: '',
  description: '',
  publisher: 'Internal',
  tags: '',
  documentation_url: '',
  is_listed: true,
  is_active: true,
  version: '1.0.0',
  transport: 'http',
  base_url: '',
  streaming: false,
  push_notifications: false,
  state_transition_history: false,
};

const toggleMode = (modes, value) =>
  modes.includes(value) ? modes.filter(m => m !== value) : [...modes, value];

const SkillForm = ({ skill, index, onChange, onRemove, canRemove }) => {
  const setSkill = (field, value) => onChange(index, { ...skill, [field]: value });

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-gray-700">Skill {index + 1}</span>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="text-xs text-red-500 hover:text-red-700 font-medium"
          >
            Remove
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">ID</label>
          <input
            type="text"
            required
            value={skill.id}
            onChange={e => setSkill('id', e.target.value)}
            placeholder="analyse"
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
          <input
            type="text"
            required
            value={skill.name}
            onChange={e => setSkill('name', e.target.value)}
            placeholder="Analyse"
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
        <input
          type="text"
          required
          value={skill.description}
          onChange={e => setSkill('description', e.target.value)}
          placeholder="Describe what this skill does"
          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Path <span className="text-gray-400 font-normal">(optional)</span></label>
        <input
          type="text"
          value={skill.path}
          onChange={e => setSkill('path', e.target.value)}
          placeholder="/analyse"
          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tags <span className="text-gray-400 font-normal">(comma-sep)</span></label>
          <input
            type="text"
            value={skill.tags}
            onChange={e => setSkill('tags', e.target.value)}
            placeholder="finance, nlp"
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Examples <span className="text-gray-400 font-normal">(comma-sep, optional)</span></label>
          <input
            type="text"
            value={skill.examples}
            onChange={e => setSkill('examples', e.target.value)}
            placeholder="Analyse this article..."
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[
          { field: 'input_modes', label: 'Input Modes' },
          { field: 'output_modes', label: 'Output Modes' },
        ].map(({ field, label }) => (
          <div key={field}>
            <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
            <div className="border border-gray-300 rounded-lg px-3 py-2 space-y-1 bg-white">
              {AGENT_IO_OPTIONS.map(opt => (
                <label key={opt} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={skill[field].includes(opt)}
                    onChange={() => setSkill(field, toggleMode(skill[field], opt))}
                    className="rounded border-gray-300 text-primaryDark focus:ring-blue-500"
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Args Schema <span className="text-gray-400 font-normal">(JSON)</span></label>
        <textarea
          rows={5}
          value={skill.args_schema}
          onChange={e => setSkill('args_schema', e.target.value)}
          spellCheck={false}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Response Schema <span className="text-gray-400 font-normal">(JSON)</span></label>
        <textarea
          rows={4}
          value={skill.response_schema}
          onChange={e => setSkill('response_schema', e.target.value)}
          spellCheck={false}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
        />
      </div>
    </div>
  );
};

// agent prop = existing AgentMarketplaceItem for edit mode, undefined for create mode
const RegisterAgentModal = ({ onClose, onSuccess, agent, onDelete }) => {
  const isEdit = !!agent;
  const [form, setForm] = useState(isEdit ? agentToForm(agent) : DEFAULT_FORM);
  const [skills, setSkills] = useState(
    isEdit && Array.isArray(agent.skills) && agent.skills.length > 0
      ? agent.skills.map(skillToForm)
      : [newSkill()]
  );
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const updateSkill = (index, updated) =>
    setSkills(prev => prev.map((s, i) => (i === index ? updated : s)));

  const addSkill = () => setSkills(prev => [...prev, newSkill()]);

  const removeSkill = (index) =>
    setSkills(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (form.slug.trim().length < 3) { setError('Slug must be at least 3 characters.'); return; }
    if (form.description.trim().length < 5) { setError('Description must be at least 5 characters.'); return; }
    if (!isEdit && !form.created_by.trim()) { setError('Created By is required.'); return; }

    try {
      const u = new URL(form.base_url);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') throw new Error();
    } catch {
      setError('Base URL must be a valid absolute URL starting with http:// or https://');
      return;
    }

    let parsedSkills;
    try {
      parsedSkills = skills.map((s, i) => {
        const argsSchema = s.args_schema.trim() ? JSON.parse(s.args_schema) : null;
        const responseSchema = s.response_schema.trim() ? JSON.parse(s.response_schema) : null;
        if (!s.id.trim() || s.id.trim().length < 2) throw new Error(`Skill ${i + 1}: ID must be at least 2 characters.`);
        if (!s.name.trim() || s.name.trim().length < 2) throw new Error(`Skill ${i + 1}: Name must be at least 2 characters.`);
        if (!s.description.trim() || s.description.trim().length < 5) throw new Error(`Skill ${i + 1}: Description must be at least 5 characters.`);
        if (s.input_modes.length === 0) throw new Error(`Skill ${i + 1}: Select at least one input mode.`);
        if (s.output_modes.length === 0) throw new Error(`Skill ${i + 1}: Select at least one output mode.`);
        return {
          id: s.id.trim(),
          name: s.name.trim(),
          description: s.description.trim(),
          tags: s.tags.split(',').map(t => t.trim()).filter(Boolean),
          examples: s.examples.trim() ? s.examples.split(',').map(t => t.trim()).filter(Boolean) : null,
          path: (s.path ?? '').trim(),
          input_modes: s.input_modes,
          output_modes: s.output_modes,
          args_schema: argsSchema,
          response_schema: responseSchema,
        };
      });
    } catch (err) {
      setError(err.message || 'Invalid skill data.');
      return;
    }

    const payload = {
      agent_type: form.agent_type,
      created_by: form.created_by.trim() || (isEdit ? 'edit' : ''),
      agent: {
        slug: form.slug,
        name: form.name,
        description: form.description,
        publisher: form.publisher,
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
        documentation_url: form.documentation_url.trim() || null,
        is_listed: form.is_listed,
        is_active: form.is_active,
      },
      agent_version: {
        version: form.version,
        transport: form.transport,
        base_url: form.base_url,
        capabilities: {
          streaming: form.streaming,
          push_notifications: form.push_notifications,
          state_transition_history: form.state_transition_history,
        },
        skills: parsedSkills,
        is_current: true,
      },
    };

    try {
      setSubmitting(true);
      await registerAgent(payload);
      onSuccess();
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'register'} agent.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit || !onDelete || !agent) return;
    try {
      setDeleting(true);
      setError(null);
      const deleted = await onDelete(agent);
      if (deleted) {
        onClose();
        onSuccess();
      }
    } catch (err) {
      setError(err.message || 'Failed to delete agent.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? `Edit Agent — ${agent.name}` : 'Register New Agent'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-4 space-y-5">

          {/* Row: agent_type + created_by */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent Type</label>
              <select
                value={form.agent_type}
                onChange={e => set('agent_type', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="internal">Internal</option>
                <option value="external">External</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Created By {isEdit && <span className="text-gray-400 font-normal">(optional for edit)</span>}
              </label>
              <input
                type="text"
                required={!isEdit}
                value={form.created_by}
                onChange={e => set('created_by', e.target.value)}
                placeholder="Full name"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Row: name + slug */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="SQL Query Agent"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Slug {isEdit && <span className="text-gray-400 font-normal">(read-only)</span>}
              </label>
              <input
                type="text"
                required
                minLength={3}
                value={form.slug}
                onChange={e => !isEdit && set('slug', e.target.value)}
                readOnly={isEdit}
                placeholder="internal.sql-query"
                className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEdit ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              required
              minLength={5}
              rows={2}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Describe what this agent does..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Row: publisher + version + transport */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Publisher</label>
              <input
                type="text"
                value={form.publisher}
                onChange={e => set('publisher', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
              <input
                type="text"
                required
                value={form.version}
                onChange={e => set('version', e.target.value)}
                placeholder="1.0.0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Transport</label>
              <select
                value={form.transport}
                onChange={e => set('transport', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="http">http</option>
                <option value="grpc">grpc</option>
                <option value="jsonrpc">jsonrpc</option>
              </select>
            </div>
          </div>

          {/* Base URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Base URL</label>
            <input
              type="url"
              required
              value={form.base_url}
              onChange={e => set('base_url', e.target.value)}
              placeholder="http://host:port/api/v1/agent/endpoint"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Row: tags + documentation_url */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags <span className="text-gray-400 font-normal">(comma-sep)</span></label>
              <input
                type="text"
                value={form.tags}
                onChange={e => set('tags', e.target.value)}
                placeholder="finance, sentiment, news"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Documentation URL <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="url"
                value={form.documentation_url}
                onChange={e => set('documentation_url', e.target.value)}
                placeholder="https://docs.example.com/agent"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Capabilities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Capabilities</label>
            <div className="flex gap-6">
              {[
                { key: 'streaming', label: 'Streaming' },
                { key: 'push_notifications', label: 'Push Notifications' },
                { key: 'state_transition_history', label: 'State History' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form[key]}
                    onChange={e => set(key, e.target.checked)}
                    className="rounded border-gray-300 text-primaryDark focus:ring-blue-500"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_listed}
                onChange={e => set('is_listed', e.target.checked)}
                className="rounded border-gray-300 text-primaryDark focus:ring-blue-500"
              />
              Listed
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={e => set('is_active', e.target.checked)}
                className="rounded border-gray-300 text-primaryDark focus:ring-blue-500"
              />
              Active
            </label>
          </div>

          {/* Skills */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Skills</label>
              <button
                type="button"
                onClick={addSkill}
                className="text-xs font-semibold text-primaryDark hover:text-primaryDark"
              >
                + Add Skill
              </button>
            </div>
            <div className="space-y-4">
              {skills.map((skill, i) => (
                <SkillForm
                  key={i}
                  skill={skill}
                  index={i}
                  onChange={updateSkill}
                  onRemove={removeSkill}
                  canRemove={skills.length > 1}
                />
              ))}
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          {isEdit && onDelete && (
            <button
              type="button"
              disabled={submitting || deleting}
              onClick={handleDelete}
              className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl transition-colors"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || deleting}
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-xl transition-colors"
          >
            {submitting ? (isEdit ? 'Saving…' : 'Registering…') : (isEdit ? 'Save Changes' : 'Register Agent')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterAgentModal;
